import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import cx from "classnames";
import _ from "lodash";

import { fetchVariables } from "actions/variables";
import {
  createPageAliases,
  deletePageAlias,
  updatePageAliases,
} from "actions/aliases";
import {
  createPersonalizedAliases,
  fetchPersonalizationLink,
  invalidatePersonalizationState,
  updatePersonalization,
  updatePersonalizationAliases,
} from "actions/personalization";

import {
  Form,
  Button,
  Dropdown,
  OverlayTrigger,
  Popover,
  Tab,
  Nav,
  ListGroup,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { GenerateLinkView } from "../modals";
import { LoadingMessage } from "pages/storyboards/storyboardSettings/leadReports/components";
import { searchPages } from "actions/pages";
import { FailureToast, SuccessToast } from "components/Toast";
import { DeleteAliasesModal } from "pages/pages/components";
import { padNumber } from "services/util";
import TruncatedTextWithTooltip from "components/TruncatedTextWithTooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import {
  ColorPickerVariable,
  ImageVaribale,
  TextVariableInput,
  VideoVaribale,
} from "../variables";

import styles from "./personalizationSettings.module.scss";

export const PersonalizationSettings = React.memo(({ storyboard, activeLead }) => {
  const dispatch = useDispatch();

  const variablesData = useSelector((state) =>
    _.get(state, "variables.items", [])
  );
  const storyboardItem = useSelector((state) =>
    _.get(state, "storyboards.storyboard.item")
  );
  const storyboardType = _.get(storyboardItem, 'type', '');
  const interactivePage = useSelector((state) =>
    _.get(state, "storyboards.storyboard.item.page", {})
  );

  const creatingPersonalizationLink = useSelector((state) =>
    _.get(state, `personalization.links.creating`, false)
  );

  const loadingVariable = useSelector((state) =>
    _.get(state, "variables.loading", [])
  );

  const [filter, setFilter] = useState(null);
  const [state, setState] = useState({
    variables: [],
    links: null,
  });
  const [activeTab, setActiveTab] = useState("values");
  const [showModal, setShowModal] = useState(false);

  const updating = useSelector((state) => {
    if (activeTab === "values") {
      return _.get(state, "personalization.leads.update.updating", false);
    } else if (activeTab === "links") {
      return _.get(state, "personalization.links.update.updating", false);
    }
    return false;
  });

  const updateState = useSelector((state) => {
    if (activeTab === "values") {
      return _.get(state, "personalization.leads.update", {});
    } else if (activeTab === "links") {
      return _.get(state, "personalization.links.update", {});
    }
    return {};
  });

  const [selectedSortFilters, setSelectedSortFilters] = useState({
    sortType: "modified_date",
    sortOrder: "DESC",
  });

  const sortFilterTypeOption = [
    {
      label: "Name",
      value: "name",
    },
    {
      label: "Date",
      value: "modified_date",
    },
  ];

  const filterTypeOptions = [
    { label: "Text", value: "text" },
    { label: "Image", value: "image" },
    { label: "Color", value: "color" },
    { label: "Video", value: "video" },
  ];

  const [editable, setEditable] = useState(null);

  const handleEditable = (activeField) => {
    if(!_.isEqual(editable, activeField)) {
      setEditable(activeField);
    }
  };

  const handleChangeFilter = (filtervalue) => {
    if (!_.isEqual(filter, filtervalue)) {
      setFilter(filtervalue);
    }
  };

  const handleSearchPages = () => {
    var request = {
      storyboard: storyboard,
    };
    dispatch(searchPages(request, 0, 50));
  };

  const handleGenerateLinkCanvas = () => {
    if (storyboardType !== "INTERACTIVE_CONTENT") {
      setShowModal(true);
      return;
    }

    const request = {
      type: "page",
      autoCreate: true,
      canonical: false,
      deletable: false,
      target: interactivePage.id,
      personalization: activeLead.id,
      contact: _.get(activeLead, "contact.id"),
    };
    dispatch(createPersonalizedAliases(request));
  };

  const handleSavePersonalization = () => {
    switch (activeTab) {
      case "values":
        if (!_.isEmpty(state.variables)) {
          const leadMappings = _.get(activeLead, "mappings", []) || [];
  
          const getVariableValue = (id) => {
            const found = leadMappings.find((m) => m.variable === id);
            return found?.value ?? undefined;
          };

          const baseMappings = variablesData.map((variable) => {
            const existingValue = getVariableValue(variable.id);
            const mapping = {
              variable: variable.id,
              type: variable.type,
              value: existingValue !== undefined ? existingValue : "",
            };
  
            if (!variable.userDefined) {
              mapping.uid = variable.name.toLowerCase().replace(/\s+/g, "");
            }

            return mapping;
          });

          // Override values from state.variables
          state.variables.forEach((updated) => {
            const match = baseMappings.find((m) => m.variable === updated.variable);
            if (match) {
              match.value =
                updated.value &&
                typeof updated.value === "object" &&
                (_.lowerCase(updated.value.type) === "image" || _.lowerCase(updated.value.type) === "video")
                  ? updated.value.id
                  : updated.value;
              match.override = !updated.value ? false :  true // track if this was overridden
            }
          });
  
          // Filter: keep all userDefined=true + overridden userDefined=false
          const filteredMappings = baseMappings
            .filter((mapping) => {
              const original = variablesData.find((v) => v.id === mapping.variable);
              const changed = state.variables.find(o => o.variable === original.id);
              const overrideVariable = leadMappings.find(o => o.variable === original.id);
              if (!original.userDefined && (changed || overrideVariable)){
                return true;
              }
              if (original.userDefined) return true;
              return mapping.override; // only keep non-userDefined if overridden
            })
            .map((mapping) => ({
              ...mapping,
              value:
                mapping.value &&
                typeof mapping.value === "object" &&
                (_.lowerCase(mapping.value.type) === "image" || _.lowerCase(mapping.value.type) === "video")
                  ? mapping.value.id
                  : mapping.value,
              override: !mapping.value ? false : true,
            }));
  
          const request = {
            id: activeLead.id,
            storyboard,
            mappings: filteredMappings,
          };

          dispatch(updatePersonalization(request));
        }
        break;
  
      case "links":
        if (!_.isEmpty(state.links)) {
          dispatch(updatePersonalizationAliases(activeLead.id, _.get(state, "links.id"), state.links));
        }
        break;
    }
  };
  

  const updateChanges = (request) => {
    if (!request) {
      return;
    }

    if (activeTab === "values") {
      const { variable, value, type } = request;

      setState((prevState) => {
        const prevVariables = prevState.variables || [];
        const index = prevVariables.findIndex(
          (item) => item?.variable === variable.id
        );

        let updatedVariables;

        if (index !== -1) {
          // exists: only update value
          updatedVariables = [...prevVariables];
          updatedVariables[index] = {
            ...updatedVariables[index],
            overeride: true,
            value,
          };
        } else {
          // not exists: add new
          const newItem = {
            variable: variable.id,
            overeride: true,
            type: type,
            value,
          };

          if (variable.userDefined === false) {
            newItem.uid = variable.name.toLowerCase().replace(/\s+/g, "");
          }

          updatedVariables = [...prevVariables, newItem];
        }

        return {
          ...prevState,
          variables: updatedVariables,
        };
      });

      return;
    }

    setState((state) => {
      return {
        ...state,
        links: request,
      };
    });
  };

  useEffect(() => {
    if (creatingPersonalizationLink) {
      setActiveTab("links");
    }
  }, [creatingPersonalizationLink]);

  useEffect(() => {
    if (activeTab == "values") {
      setState((state) => {
        return {
          ...state,
          links: null,
        };
      });
    }
  }, [activeTab]);

  const onFilterChange = (type, value) => {
    setSelectedSortFilters((state) => {
      return {
        ...state,
        [type]: value,
      };
    });
  };

  useEffect(() => {
    handleFetchVariables();
  }, [selectedSortFilters]);

  const renderIcon = (fieldType) => {
    let iconClassName = "cleverstory-text-layer";

    // Determine additional class based on fieldType

    switch (fieldType) {
      case "COLOR":
        iconClassName = "cleverstory-font-color";
        break;

      case "TEXT":
        iconClassName = "cleverstory-text-layer";
        break;

      case "IMAGE":
        iconClassName = "cleverstory-background";
        break;

      case "VIDEO":
        iconClassName = "cleverstory-video";
        break;
    }

    return iconClassName;
  };

  const handleReset = () => {
    if (activeTab === "values") {
      setState((state) => {
        return {
          ...state,
          variables: [],
        };
      });
      return;
    }

    setState((state) => {
      return {
        ...state,
        links: null,
      };
    });
  };

  useEffect(() => {
    if (!updateState) {
      return;
    }

    if (updateState.success) {
      toast(
        <SuccessToast
          heading={`Success`}
          description={
            activeTab == "values"
              ? `variables saved successfully`
              : "url saved successfully"
          }
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );

      setState((state) => {
        if(activeTab === "values") {
          return {
            ...state,
            variables: [],
          };
        }
        return {
          ...state,
          links: null,
        };
      })

      dispatch(
        invalidatePersonalizationState(
          activeTab === "values" ? "leads" : "links"
        )
      );
    } else if (updateState.error) {
      const errorData = _.head(updateState.error);

      if (_.get(errorData, "code")) {
        toast(
          <FailureToast
            heading={`Failure`}
            description={
              activeTab == "values"
                ? `Unable to save variables`
                : `Unable to save ${_.get(errorData, "message")}`
            }
          />,
          {
            type: toast.TYPE.DEFAULT,
            position: toast.POSITION.BOTTOM_RIGHT,
            autoClose: 3000,
          }
        );
        handleReset()
        dispatch(
          invalidatePersonalizationState(
            activeTab === "values" ? "leads" : "links"
          )
        );
      }
    }
  }, [updateState]);


  useEffect(()=>{
    handleReset()
  },[activeLead])

  const activeDropdownLabel = _.find(sortFilterTypeOption, (data) => {
    return data.value == selectedSortFilters.sortType;
  });

  useEffect(() => {
    handleFetchVariables();
    handleSearchPages();
  }, [storyboard]);

  const handleFetchVariables = () => {
    if (storyboard) {
      var request = {
        storyboard: storyboard,
        sort: selectedSortFilters.sortType,
        order: selectedSortFilters.sortOrder,
      };
      dispatch(fetchVariables(request, 0, 50));
    }
  };

  return (
    <div key={storyboard.id} className={styles.leadsListContainer}>
      <Tab.Container
        id="user-management-tab"
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        defaultActiveKey="values"
      >
        <header className={styles.userManagementHeader}>
          <Nav variant="pills" className={styles.userManagementTab}>
            <Nav.Item>
              <Nav.Link eventKey="values">Values</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="links">Links</Nav.Link>
            </Nav.Item>
          </Nav>

          <section
            className={cx(styles.buttonContainer, styles.primaryActions)}
          >
            {/* <Button variant="transparent" className={styles.previewBtn}>
              <i className={cx("cleverstory-view")} />
              <span>Preview</span>
            </Button> */}
            <Button
              variant="primary"
              disabled={creatingPersonalizationLink}
              onClick={() => {
                handleGenerateLinkCanvas();
              }}
              className={styles.generateLinkButton}
            >
              <i className={cx(styles.icon, "cleverstory-link")} />
              {creatingPersonalizationLink
                ? "Generating Link...."
                : "Generate Link"}
            </Button>
          </section>
        </header>
        <Tab.Content className={styles.tabContainer}>
          <Tab.Pane className={styles.tabPanes} eventKey="values">
            <div className={styles.filterActionContainer}>
              <div className={cx(styles.options)}>
                <span className={styles.label}>Filter by:</span>
                <Dropdown className={cx(styles.filterOptionDropdown)}>
                  <Dropdown.Toggle>
                    {filter ? _.get(filter, "label", "All") : "All"}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="min-width-0">
                    <Dropdown.Item
                      active={!filter}
                      onClick={() => {
                        handleChangeFilter(null);
                      }}
                    >
                      All
                    </Dropdown.Item>
                    {_.map(filterTypeOptions, (option) => {
                      return (
                        <Dropdown.Item
                          active={option.value === filter?.value}
                          onClick={() => {
                            handleChangeFilter(option);
                          }}
                        >
                          {option.label}
                        </Dropdown.Item>
                      );
                    })}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
              <div className={styles.conditions}>
                <span className={styles.label}>
                  <label className={styles.filterLabel}>Sort by:</label>
                  <label className={styles.filterLabel}>
                    {selectedSortFilters.sortOrder && (
                      <img
                        onClick={() =>
                          onFilterChange(
                            "sortOrder",
                            selectedSortFilters.sortOrder === "ASC"
                              ? "DESC"
                              : "ASC"
                          )
                        }
                        className={cx(
                          styles.sortIcon,
                          selectedSortFilters.sortOrder === "ASC"
                            ? styles.ascending
                            : styles.descending
                        )}
                        alt="sortIcon"
                        src="/images/sortIcon.svg"
                      />
                    )}
                  </label>
                </span>

                <Dropdown className={styles.sortDropdown}>
                  <Dropdown.Toggle className={styles.selected}>
                    <Button
                      size="sm"
                      variant="primary"
                      className={styles.button}
                    >
                      <span className={styles.filterType}>
                        {activeDropdownLabel.label}
                      </span>
                      <i className={cx(styles.icon, "cleverstory-drop-down")} />
                    </Button>
                  </Dropdown.Toggle>
                  <Dropdown.Menu className={styles.typesMenu}>
                    {sortFilterTypeOption.map((option, index) => {
                      return (
                        <Dropdown.Item
                          active={option.value == selectedSortFilters.sortType}
                          onClick={() =>
                            onFilterChange("sortType", option.value)
                          }
                          key={index}
                          eventKey={index}
                        >
                          {option.label}
                        </Dropdown.Item>
                      );
                    })}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>
            {loadingVariable && variablesData && variablesData.length === 0 && (
              <div className={cx(styles.loader, styles.modalLoader)}>
                <LoadingMessage className={styles.loadingMessage} />
              </div>
            )}
            {variablesData && variablesData.length > 0 && (
              <div className={styles.variablesList}>
                {variablesData
                  .filter((variable) => {
                    if (!filter) return true;
                    return (
                      variable.type?.toLowerCase() ===
                      filter.value.toLowerCase()
                    );
                  })
                  .map((variable) => (
                    <div
                      key={variable.id || variable.name}
                      className={styles.container}
                    >
                      <span className={styles.label}>
                        <i className={renderIcon(variable.type)} />
                        {variable.name}
                      </span>
                      <RenderVariableElement
                        variable={variable}
                        activeLead={activeLead}
                        storyboard={storyboard}
                        storyboardItem={storyboardItem}
                        updatedValue={state.variables}
                        updateChanges={updateChanges}
                      />
                    </div>
                  ))}
              </div>
            )}
            <footer className={styles.userManagementFooter}>
              <div className={styles.footerAction}>
                <Button
                  variant="secondary"
                  disabled={activeTab === "values" ? _.isEmpty(state.variables) : _.isEmpty(state.links)}
                  onClick={handleReset}
                  className={styles.cancelButton}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  disabled={updating || activeTab === "values" ? _.isEmpty(state.variables) : _.isEmpty(state.links)}
                  onClick={() => {
                    handleSavePersonalization();
                  }}
                  className={styles.saveButton}
                >
                  {updating ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </footer>
          </Tab.Pane>

          <Tab.Pane className={styles.tabPanes} eventKey="links">
            {!activeLead && (
              <div className={styles.emptyMediaContainer}>
                <div className={styles.emptyMessage}>
                  <div className={styles.errorMessageContent}>
                    <div className={styles.errorMessageTitle}>
                      <h2 className={styles.heading}>Weather forecast</h2>
                    </div>
                    <div className={styles.errorMessageSubText}>
                      <span>
                        Clear skies and <br /> empty screens
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {activeLead && (
              <ManagePersonalizedLinks
                activeLead={activeLead}
                editable={editable}
                handleEditable={handleEditable}
                updatedValue={state.links}
                handleReset={handleReset}
                updateChanges={updateChanges}
              />
            )}
            <footer className={styles.userManagementFooter}>
              <div className={styles.footerAction}>
                <Button
                  variant="secondary"
                  disabled={ _.isEmpty(state.links)}
                  onClick={handleReset}
                  className={styles.cancelButton}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  disabled={updating || editable || _.isEmpty(state.links)}
                  onClick={() => {
                    handleSavePersonalization();
                  }}
                  className={styles.saveButton}
                >
                  {updating ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </footer>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      {activeLead && (
        <GenerateLinkView
          show={showModal}
          activeLead={activeLead}
          onClose={() => {
            setShowModal(false);
          }}
        />
      )}
    </div>
  );
});

const ManagePersonalizedLinks = React.memo(
  ({ activeLead, updateChanges, updatedValue, handleReset, editable, handleEditable }) => {
    const dispatch = useDispatch();

    const personalizationLinks = useSelector((state) =>
      _.get(state, `personalization.links.items.${activeLead.id}`, [])
    );
    const storyboard = useSelector((state) =>
      _.get(state, "storyboards.storyboard.item", null)
    );
    const workspace = useSelector((state) =>
      _.get(state, "workspaces.workspace.item", null)
    );
    const domain = _.get(workspace, "defaultDomain", {});
    let domainUrl = _.get(storyboard, "domain") || domain;

    const loading = useSelector((state) =>
      _.get(state, `personalization.links.loading`, false)
    );

    useEffect(() => {
      handleFetchLinks(0);
      handleEditable(null);
    }, [activeLead]);

    const handleFetchLinks = (page) => {
      if (activeLead) {
        dispatch(fetchPersonalizationLink(activeLead.id, page, 20));
      }
    };

    return (
      <div className={styles.linksWrapper}>
        {loading &&
          personalizationLinks &&
          personalizationLinks.length === 0 && (
            <div className={cx(styles.loader, styles.modalLoader)}>
              <LoadingMessage className={styles.loadingMessage} />
            </div>
          )}

        {!loading && personalizationLinks.length === 0 && (
          <div className={styles.emptyMediaContainer}>
            <div className={styles.emptyMessage}>
              <div className={styles.errorMessageContent}>
                <div className={styles.errorMessageTitle}>
                  <h2 className={styles.heading}>Weather forecast</h2>
                </div>
                <div className={styles.errorMessageSubText}>
                  <span>
                    Clear skies and <br /> empty screens
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {personalizationLinks.length > 0 &&
          personalizationLinks.map((item) => {
            return (
              <RenderLinks
                item={item}
                key={item.id}
                editable={editable}
                handleReset={handleReset}
                handleEditable={handleEditable}
                domainUrl={domainUrl}
                updatedValue={updatedValue}
                updateChanges={updateChanges}
              />
            );
          })}
      </div>
    );
  }
);

const RenderVariableElement = React.memo(
  ({ variable, storyboard, activeLead, updateChanges, updatedValue, storyboardItem }) => {
    const { type } = variable;
    const [value, setValue] = useState(null);

    const handleChange = (value, fieldType) => {
      const requestUpdate = {
        variable: variable,
        type: fieldType,
        value: value,
      };
      setValue(value);
      updateChanges(requestUpdate);
    };

    useEffect(() => {
      handleResetValues();
    }, [activeLead?.id]);

    useEffect(() => {
      if (_.isEmpty(updatedValue)) {
        handleResetValues();
      }
    }, [updatedValue]);

    const handleResetValues = () => {
      setValue(null);
      const leadMappings = _.get(activeLead, "mappings", []) || [];
    
      const existingValue = getVariableValue(variable.id, leadMappings);
    
      if (existingValue !== undefined && existingValue !== null) {
        setValue(existingValue);
        return;
      }

      if (!variable.userDefined && activeLead) {
        const contactValue = getContactValue(variable.name, activeLead?.contact);
        if (contactValue !== undefined && contactValue !== null) {
          setValue(contactValue);
        }
      }
    };
    
    const getVariableValue = (variableId, changeValues) => {
      const found = changeValues?.find((item) => item?.variable === variableId);
      return found ? found.value : null;
    };
    
    // Fallback to contacts for non-userDefined variables
    const getContactValue = (fieldName, contact = {}) => {
      const key = Object.keys(contact).find(
        (k) => k.toLowerCase().replace(/\s+/g, '') === fieldName.toLowerCase().replace(/\s+/g, '')
      );
      return key ? contact[key] : null;
    };
    

    switch (type) {
      case "TEXT":
        return (
          <TextVariableInput
            value={value}
            key={variable.id}
            onChange={(e) => {
              setValue(e.target.value);
            }}
            onBlur={() => {
                const requestUpdate = {
                  variable: variable,
                  type: type,
                  value: value,
                };
                updateChanges(requestUpdate);
            }}
          />
        );

      case "COLOR":
        return (
          <ColorPickerVariable
            key={variable.id}
            value={
              value
                ? value
                : {
                    r: 255,
                    g: 255,
                    b: 255,
                    a: 1,
                    type: "solid",
                  }
            }
            onChange={(color) => {
              handleChange(color, type);
            }}
          />
        );

      case "IMAGE":
        return (
          <ImageVaribale
            key={variable.id}
            value={value}
            storyboard={storyboardItem}
            onChange={(image) => {
              handleChange(image, type);
            }}
          />
        );

      case "VIDEO":
        return (
          <VideoVaribale
            key={variable.id}
            value={value}
            storyboard={storyboardItem}
            onChange={(video) => {
              handleChange(video, type);
            }}
          />
        );

      default:
        return (
          <TextVariableInput
            value={value}
            key={variable.id}
            onChange={(e) => {
              setValue(e.target.value);
            }}
            onBlur={() => {
                const requestUpdate = {
                  variable: variable,
                  type: type,
                  value: value,
                };
                updateChanges(requestUpdate);
            }}
          />
        );
    }
  }
);

const RenderLinks = React.memo(({ item, key, editable, handleEditable, handleReset, domainUrl, updateChanges, updatedValue }) => {
  const dispatch = useDispatch();
  const workspace = useSelector(state => _.get(state, 'workspaces.workspace.item', null));
  const pageId = _.get(item, 'target.id', null);
  const [state, setState] = useState({
    url: '',
    validation: null,
  });

  useEffect(()=>{
    setState((state)=>{
      return {
        ...state,
        url: item.url
      }
    })
  },[item])

  const [deleteModal, showDeleteModal] = useState(false);
  const inputRef = React.useRef(null);
  const views = _.get(item, 'views', 0);

  const renderStoryboardName = (storyboardType) => {
    switch (storyboardType) {
      case "LANDING_PAGE":
        return "Landing Page";
      case "RESOURCE_CENTER":
        return "Resource Center";
      case "INTERACTIVE_CONTENT":
        return "Interactive Content";
      case "SPATIAL":
        return "Spatial";
      default:
        return "Landing Page";
    }
  };

  const _renderTimeSpent = (seconds) => {
    let secondsPerView = seconds / views || 0;
    let minutes = padNumber(Math.floor(secondsPerView / 60), 2);
    secondsPerView = padNumber(Math.floor(secondsPerView % 60), 2);
    var hours = padNumber(Math.floor(minutes / 60), 2);
    minutes = padNumber(minutes % 60, 2);

    hours = hours > 0 ? `${hours}:` : "";
    return `${hours}${minutes}:${secondsPerView}`;
  };

  const handleUpdateUrl = () => {
    var targetValue = _.get(item, "target");

    if (typeof targetValue !== "string") {
      targetValue = _.get(targetValue, "id");
    }

    const request = {
      ...item,
      autoCreate: false,
      target: targetValue,
      url: state.url,
    };
    const error = validateForm(request);

    setState((state) => {
      return {
        ...state,
        validation: error,
      };
    });

    if (error) {
      return;
    }
    updateChanges(request);
    handleEditable(null);
  };

  useEffect(() => {
    if (_.isEmpty(updatedValue)) {
      setState({
        url: item.url,
        validation: null,
      });
    }
  }, [updatedValue]);

  const handleDelete = () => {
    var targetValue = _.get(item, "target");

    if (typeof targetValue !== "string") {
      targetValue = _.get(targetValue, "id");
    }

    var deleteRequest = {
      type: item.type,
      target: targetValue,
      personalization: item.personalization,
    };

    dispatch(deletePageAlias(_.get(item, "id"), deleteRequest));
  };

  const validateForm = (request) => {
    const url = _.get(request, "url");
    const autoCreate = _.get(request, "autoCreate");
    var aliasRegex =
      /^(?!.*\/\/)[a-z_-]|[`\s!@#$%^&*()+\=\[\]{};':"\\|,.<>\?~]/;
    var slashRegext = /^\/([^\/])+(?:\/[^\/]+)*$/gm;
    if (autoCreate || url === "/") {
      return null;
    }

    if (aliasRegex.test(url) || !slashRegext.test(url)) {
      return {
        url: "Please provide valid alias",
      };
    }

    if (!url || url.trim() == "") {
      return {
        url: "Alias cannot be empty",
      };
    }

    if (!url.startsWith("/")) {
      return {
        url: "Alias must start with /",
      };
    }
  };

  useEffect(() => {
    handleChangeValue();
  }, [item]);

  const handleChangeValue = () => {
    if (inputRef.current) {
      inputRef.current.value = item.url; // Set the new value
    }
  };



  const handleNameChange = (e) => {
    setState((state) => {
      return {
        ...state,
        url: e.target.value,
      };
    });
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${domainUrl.url}${state.url}`);
      toast(<SuccessToast heading={`Success`} description={"Copied url"} />, {
        type: toast.TYPE.DEFAULT,
        position: toast.POSITION.BOTTOM_RIGHT,
        autoClose: 3000,
      });
    } catch (err) {
      toast(
        <FailureToast
          heading={`Failure`}
          description={`Unable to copy url!`}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );
    }
  };

  const handlePersonalizedPreview = () => {
    if (!pageId && !workspace){
      return;
    }
    const url = `${process.env.REACT_APP_UI_URL}/app/${workspace.id}/pages/${pageId}/preview?p=${item.personalization}`;
    window.open(url);
  }

  return (
    <div key={key} className={styles.linkContainer}>
      <div className={styles.listLink}>
        <div className={styles.linkView}>
          <span className={styles.title}>
            {renderStoryboardName(item.target.storyboardType)}
          </span>
          <span
            onClick={()=>{
              if (!editable) {
                const fullUrl = `${domainUrl.url}${state.url || ""}`;
                window.open(fullUrl, "_blank");
              }
            }}
            className={cx(styles.value, { [styles.renameValue]: editable == item.id })}
          >
              <TruncatedTextWithTooltip
                content={domainUrl.url}
                maxWidth={185}
                placement="top"
              />
            <span>&nbsp;</span>
            {editable !== item.id && 
              <TruncatedTextWithTooltip
                content={state.url}
                maxWidth={100}
                placement="top"
              />
            }
            {editable == item.id && (
              <Form.Group className={styles.linkRename}>
                <Form.Control
                  type="text"
                  ref={inputRef}
                  placeholder="Enter link"
                  value={state.url}
                  onFocus={() => {
                    if (updatedValue?.id !== item.id) {
                      handleReset()
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault(); 
                      handleUpdateUrl();
                    }
                  }}
                  onChange={handleNameChange}
                  onBlur={handleUpdateUrl}
                  isInvalid={false}
                />
                {_.get(state.validation, "url") && (
                  <span className={styles.error}>
                    <FontAwesomeIcon icon={faExclamationTriangle} className={styles.icon} />
                    {_.get(state.validation, "url")}
                  </span>
                )}
              </Form.Group>
            )}
          </span>
        </div>
        {!editable && (
          <div className={styles.linkView}>
            <span className={styles.title}>Avg. Time Spent</span>
            <span className={styles.value}>
              {_renderTimeSpent(_.get(item, "totalTimeSpent", 0))}
            </span>
          </div>
        )}
        {!editable && (
          <div className={styles.linkView}>
            <span className={styles.title}># of Views</span>
            <span className={styles.value}>{_.get(item, "views", 0)}</span>
          </div>
        )}
        {!editable && (
          <div className={styles.linkActions}>
            <Button variant="info" className={styles.viewBtn} onClick={handlePersonalizedPreview}>
              <i className={cx("cleverstory-view")} />
            </Button>
            <OverlayTrigger
              rootClose={true}
              rootCloseEvent="mousedown"
              trigger="click"
              placement="bottom"
              overlay={
                <Popover className={styles.linkMoreAction} id="popover-basic">
                  <ListGroup>
                    <ListGroup.Item
                      onClick={() => handleEditable(item.id)}
                      className={styles.linkMoreList}
                    >
                      Edit url
                    </ListGroup.Item>
                    <ListGroup.Item
                      onClick={handleCopy}
                      className={styles.linkMoreList}
                    >
                      Copy link
                    </ListGroup.Item>
                    <ListGroup.Item
                      onClick={() => {
                        showDeleteModal(true);
                      }}
                      className={styles.linkMoreList}
                    >
                      Delete
                    </ListGroup.Item>
                  </ListGroup>
                </Popover>
              }
            >
              <Button variant="info" className={styles.moreButton}>
                <i className={cx("cleverstory-more", styles.iconMore)} />
              </Button>
            </OverlayTrigger>
          </div>
        )}
      </div>
      <DeleteAliasesModal
        deleteModal={deleteModal}
        showDeleteModal={showDeleteModal}
        onDelete={handleDelete}
      />
    </div>
  );
});
